import { keepPreviousData, useQuery, type UseQueryOptions } from '@tanstack/react-query';

import type { PageResponse } from '@models/api';
import { type GetProductsParams, type ProductResponse, getProducts } from '@apis/product';
import { dropEmptyValues } from '../../utils/common';

const PAGE_SIZE = 50;

export const useProductList = ({
  page,
  searchParam,
}: {
  page: number;
  searchParam: GetProductsParams;
}) => {
  const cleanParams = dropEmptyValues(searchParam, [
    'productCode',
    'productName',
    'stockId',
    'countryId',
    'productStatus',
    'productSaleType',
  ]);

  const queryOptions: UseQueryOptions<PageResponse<ProductResponse>, Error> = {
    // IMPORTANT:
    // - Include cleanParams in the query key so the list refreshes when filters change.
    // - React Query v5 hashes query keys structurally, so passing the object is safe.
    queryKey: ['products', page, cleanParams],
    queryFn: () =>
      getProducts({
        pageNo: page,
        pageSize: PAGE_SIZE,
        ...cleanParams,
      }),
    enabled: Number.isFinite(page) && page > 0,
    refetchOnMount: 'always',

    // React Query v5 replacement for keepPreviousData
    // Keeps the previous page while the next one is loading (prevents table flicker).
    placeholderData: keepPreviousData,
  };

  return useQuery(queryOptions);
};
